import { CalculateConfigurationInstitutionParticipantSourceDataAllocations, ConfigurationInstitution as ConfigurationInstitutionDto, ConfigurationStatus, SaveConfigurationInstitution, SourceDataAllocationType } from "@/api/dtos";
import InputOnlyField from "@/components/forms/fields/inputOnly/InputOnlyField";
import Text from "@/components/forms/inputs/Text";
import SetLinks from "@/components/layouts/SetLinks";
import useConfigurationInstitution from "@/hooks/data/useConfigurationInstitution";
import { FC, useCallback, useContext, useMemo } from "react";

import UneditableText from "@/components/forms/inputs/UneditableText";
import ConfigurationContextTemplate, { IConfigurationContextTemplateProps } from "@/components/forms/templates/ConfigurationContextTemplate";
import InstitutionContext from "@/contexts/data/InstitutionContext";
import TrialContext from "@/contexts/data/TrialContext";
import { ConfigurationInstitutionsInstitutionCreateLink, ConfigurationInstitutionsTrialCreateLink } from "@/contexts/layouts/LinkContext";
import { useLocation } from "react-router-dom";


const allocationTypes = [
    SourceDataAllocationType.Constant,
    SourceDataAllocationType.FirstXParticipants,
    SourceDataAllocationType.RepeatingCycle,
    SourceDataAllocationType.FirstXParticipantsAndRepeatingCycle,
    SourceDataAllocationType.Inherit
];

const statuses = [
    ConfigurationStatus.Disabled,
    ConfigurationStatus.ReadOnly,
    ConfigurationStatus.Enabled
];


const ConfigurationInstitution: FC = () => {
    const { data: trial, oid: trialOid } = useContext(TrialContext);
    const { data: institution } = useContext(InstitutionContext);

    const contextQuery = useMemo(() => {
        if (!institution?.id) {
            return undefined;
        }

        return {
            trialId: institution.trialId,
            masterGroupId: institution.masterGroupId,
            coordinatingGroupId: institution.coordinatingGroupId,
            institutionId: institution.id
        }
    }, [institution]);

    const { data: configurationInstitution, loading: configurationInstitutionLoading, validating: configurationInstitutionValidating } = useConfigurationInstitution(contextQuery, { revalidateOnFocus: false });

    const loading = configurationInstitutionLoading;
    const validating = configurationInstitutionValidating;

    const location = useLocation();

    const returnUrl = useMemo(() => {
        if (location.state?.from) {
            return `${location.state.from.pathname}${location.state.from.search}`
        }
        
        return `/${trialOid}/configuration/institutions`;
    }, [trialOid, location])

    const onSave = useCallback<IConfigurationContextTemplateProps<ConfigurationInstitutionDto>['onSave']>(async (client, data, allocations, updates) => {
        await client.post(new SaveConfigurationInstitution({ data: data, allocations: allocations, participantSourceDataAllocationUpdates: updates}));
    }, []);

    const onCalculateAllocations = useCallback<IConfigurationContextTemplateProps<ConfigurationInstitutionDto>['onCalculateAllocations']>(async (client, contextQuery, data, allocations) => {
        const response = await client.post(new CalculateConfigurationInstitutionParticipantSourceDataAllocations({ 
            ...contextQuery,
            configurationInstitution: data, 
            allocations: allocations
        }));

        return response;
    }, []);
    
    const onValidate = useCallback<IConfigurationContextTemplateProps<ConfigurationInstitutionDto>['onValidate']>(async (client, errors, formState) => {

        return errors;
    }, []);

    return (
        <>
            <SetLinks 
                createTrialLink={ConfigurationInstitutionsTrialCreateLink} 
                createInstitutionLink={ConfigurationInstitutionsInstitutionCreateLink} 
                participantLinkEnabled={false} 
            />
            <ConfigurationContextTemplate
                allocationTypes={allocationTypes}
                entityName="Institution"
                title={institution?.name}
                onCalculateAllocations={onCalculateAllocations}
                onSave={onSave}
                onValidate={onValidate}
                statuses={statuses}
                configuration={configurationInstitution}
                configurationLoading={loading}
                configurationValidating={validating}
                contextQuery={contextQuery}
                returnUrl={returnUrl}
                children={
                    <>
                        <InputOnlyField
                            propertyPath="originalName"
                            label="Default Name"
                            helperText="The Default Name of the institution."
                            margin="normal"
                            component={UneditableText}
                            disabled
                            value={institution?.defaultName}
                        />
                        <InputOnlyField
                            propertyPath="configuration.name"
                            label="Name"
                            helperText="Enter a name to override the Default Name of the institution."
                            margin="normal"
                            component={Text}
                        />
                    </>
                }
            />
        </>
    )
};

export default ConfigurationInstitution;